/**
 * A WhatsApp group's roster as the chat list carries it — symphony-bff `ListChats` `ChatSummary.wa_group`, only on
 * `isWaGroup` rows: `{ waGroupId, status, members: [{ name, state }], joinedCount }`, names only, never numbers
 * (contract "Group setup + WhatsApp-group roster" §3, 2026-11-02).
 *
 * Pure: the parse, the per-member state words for the chat header and the setup card, the header subtitle, and the
 * chat-list title. iOS and Android hold the same rules under the same i18n keys.
 *
 * The web DTO carries the row's `waGroup` as `unknown` and it is read ONLY through `parseWaGroupRoster`, so a service
 * that predates the field (or a malformed one) is simply "no roster" — the row and the header read as before.
 */

export type WaGroupStatus = 'CREATING' | 'ACTIVE' | 'ENDED';
/** INVITED = queued (not tried yet), INVITE_SENT / INVITE_FAILED = the invite outreach's outcome, JOINED = in the group. */
export type WaGroupMemberState = 'INVITED' | 'INVITE_SENT' | 'INVITE_FAILED' | 'JOINED';

export interface WaGroupRosterMember {
  name: string;
  state: WaGroupMemberState;
}

export interface WaGroupRoster {
  waGroupId: string;
  status: WaGroupStatus;
  members: WaGroupRosterMember[];
  joinedCount: number;
}

type Translate = (key: string, params?: Record<string, string | number>) => string;

const isObject = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);
const MEMBER_STATES: readonly WaGroupMemberState[] = ['INVITED', 'INVITE_SENT', 'INVITE_FAILED', 'JOINED'];

/** The row's `waGroup`, or null. An unknown member state reads as INVITED (queued) — never as joined. */
export function parseWaGroupRoster(raw: unknown): WaGroupRoster | null {
  if (!isObject(raw)) return null;
  const waGroupId = typeof raw.waGroupId === 'string' ? raw.waGroupId.trim() : '';
  if (!waGroupId) return null;
  const status: WaGroupStatus = raw.status === 'CREATING' || raw.status === 'ENDED' ? raw.status : 'ACTIVE';
  const members = (Array.isArray(raw.members) ? raw.members : []).flatMap((m): WaGroupRosterMember[] => {
    if (!isObject(m)) return [];
    const name = typeof m.name === 'string' ? m.name.trim() : '';
    if (!name) return [];
    const state = (MEMBER_STATES as readonly unknown[]).includes(m.state) ? (m.state as WaGroupMemberState) : 'INVITED';
    return [{ name, state }];
  });
  const joined = typeof raw.joinedCount === 'number' && Number.isFinite(raw.joinedCount) && raw.joinedCount >= 0
    ? Math.floor(raw.joinedCount)
    : members.filter((m) => m.state === 'JOINED').length;
  return { waGroupId, status, members, joinedCount: joined };
}

/** The short state word in the chat header ("Arava · invited"). */
export function waGroupHeaderStateKey(state: WaGroupMemberState): string {
  switch (state) {
    case 'JOINED': return 'whatsapp.group.member.joined';
    case 'INVITE_SENT': return 'whatsapp.group.member.sent';
    case 'INVITE_FAILED': return 'whatsapp.group.member.failed';
    default: return 'whatsapp.group.member.invited';
  }
}

/** The state line on the setup card's created state ("Invite queued", "Invite sent", …). */
export function waGroupCardStateKey(state: WaGroupMemberState): string {
  switch (state) {
    case 'JOINED': return 'chat.groupSetup.state.joined';
    case 'INVITE_SENT': return 'chat.groupSetup.state.sent';
    case 'INVITE_FAILED': return 'chat.groupSetup.state.failed';
    default: return 'chat.groupSetup.state.queued';
  }
}

/** "Arava · invited, Dana · joined" — the people part of the header subtitle; '' when nobody was invited. */
export function waGroupPeople(roster: WaGroupRoster, t: Translate): string {
  return roster.members.map((m) => `${m.name} · ${t(waGroupHeaderStateKey(m.state))}`).join(', ');
}

/** The WhatsApp-group chat header's second line: "WhatsApp group · {people}", or "… · nobody invited yet". */
export function waGroupHeaderSubtitle(roster: WaGroupRoster | null, t: Translate): string {
  const people = roster ? waGroupPeople(roster, t) : '';
  return people ? t('whatsapp.group.header.subtitle', { people }) : t('whatsapp.group.header.nobody');
}

/**
 * A WhatsApp group's chat-list title: "<name> · WhatsApp group", so it never reads as the same-named Symphony group.
 * `label` is the translated "WhatsApp group"; a title that already ends with it is left alone.
 */
export function waGroupRowTitle(title: string, label: string): string {
  const name = title.trim();
  if (!name) return label;
  return name.endsWith(` · ${label}`) ? name : `${name} · ${label}`;
}

/** The web route of one WhatsApp group's screen (the `settings/whatsapp/group/{id}` app link). */
export function waGroupScreenPath(waGroupId: string): string {
  return `/settings/connectors/channels/whatsapp/group/${encodeURIComponent(waGroupId)}`;
}
